import React, { useContext, useMemo } from 'react';

import { useMembership } from '@ocx/data-membership';
import { diffDateTime, durationDateTime } from '@ocx/utils-date';
import { LocalOffersLocationContext } from '@ocx/feat-local-offers';
import { canVoucherBePurchased, isPurchaseVoucherOffer, Offer } from '@ocx/data-offers';
import {
  getOfferCardAccessoryType,
  getOfferCardMarketingContent,
  getOfferCardMemberProgress,
  getOfferCardPointsDiff,
  getOfferLabel,
  OfferCardProps,
} from '@ocx/ui-offers';
// eslint-disable-next-line @nx/enforce-module-boundaries -- app configuration
import { useConfiguration } from '@ocx-app/modules/configuration/useConfiguration';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { impressionTrackingCtxFromOffer } from '@ocx-app/modules/offers/tracking.types';

import { useGetOfferOnClick } from '../use-get-offer-on-click.hook';
import { useGetOfferLoyaltyAction } from '../use-get-offer-loyalty-action.hook';

export const getOfferExpiresInHumanized = (offerExpiresAt: string): string => {
  const offerExpiresAtDate = new Date(offerExpiresAt);
  const diffMinutes = Math.abs(diffDateTime(new Date(), offerExpiresAtDate, 'minute'));
  if (diffMinutes < 60) {
    return '1 hour';
  }

  return durationDateTime(diffMinutes, 'minute');
};

export type OfferCardControllerProps = {
  children: (value: OfferCardProps) => React.ReactElement;
  offer: Offer;
};

export const OfferCardController = ({ children, offer }: OfferCardControllerProps) => {
  const pointsStrategy = useConfiguration('loyaltyProgram.pointsStrategy');
  const { membership } = useMembership();

  const contextData = useContext(LocalOffersLocationContext);
  const locationState = contextData?.locationState ?? null;
  const isAllOffersSelected = locationState === 'all';

  const getOfferLoyaltyAction = useGetOfferLoyaltyAction({
    membershipIsCompleted: membership.isCompleted,
    membershipPoints: membership.points,
  });
  const getOfferOnClick = useGetOfferOnClick({
    pointsStrategy,
  });

  const value: OfferCardProps = useMemo(() => {
    const onClick = getOfferOnClick(offer);
    const loyaltyAction = getOfferLoyaltyAction(offer);
    const onActionClick = loyaltyAction.state === 'hasAction' ? loyaltyAction.onClick : undefined;
    const actionSubmitting = loyaltyAction.state === 'hasAction' ? loyaltyAction.isLoading : false;

    const expiresIn = offer.expireAt ? getOfferExpiresInHumanized(offer.expireAt) : null;
    const marketingContent = getOfferCardMarketingContent(offer);
    const label = getOfferLabel(offer);
    const memberOfferProgress = getOfferCardMemberProgress(offer);
    const pointsDiff = getOfferCardPointsDiff(offer, { pointsStrategy });
    const actionVariant = getOfferCardAccessoryType({
      hasOnClick: !!onClick,
      hasOnActionClick: !!onActionClick,
      activationStatus: offer.activationStatus,
      hasProgress: !!memberOfferProgress?.canShowMemberProgress,
      loading: actionSubmitting,
    });

    const accessoryButtonDisabled = isPurchaseVoucherOffer(offer)
      ? !canVoucherBePurchased(offer, { membershipPoints: membership.points }).canPurchase
      : false;

    return {
      accessoryButtonDisabled,
      loading: actionSubmitting,
      impressionTrackingCtx: impressionTrackingCtxFromOffer(offer),
      expiresIn,
      marketingContent,
      label,
      progress: memberOfferProgress,
      pointsDiff,
      onClick,
      accessoryType: actionVariant,
      onAccessoryButtonClick: onActionClick,
      pointsStrategy,
      availableAtLocations: isAllOffersSelected ? [] : offer.availableAtLocations,
    };
  }, [getOfferLoyaltyAction, getOfferOnClick, isAllOffersSelected, offer, pointsStrategy, membership.points]);

  return children(value);
};
